// Review step 2 · Facts & relations (PLAN §6, task 19): what the reviewer decides for each extracted claim, how an
// extracted claim ("funded by", "CEO of", "date of birth" as the model wrote them) becomes a draft for the graph
// (a field of the subject's type or a relation type, with value, period, amount, kind, attribution and the
// reliability grade), and the checks a draft must pass before it can be accepted. Pure functions: the API, the web
// app and the tests share them. Nothing here writes: step 2 decisions are staged on the candidates and committed
// through the claims service when the step completes.

import {
  ATTRIBUTION_LEVELS,
  type AttributionLevel,
  CLAIM_KINDS,
  type ClaimInput,
  type ClaimKind,
  isCredibility,
  isSourceReliability,
  type SourceReliability,
  VERIFICATION_FLAGS,
  type VerificationFlag,
} from './claims';
import { parseFuzzyDate } from './fuzzy-date';
import { foldText } from './schema/match';
import { groupsForType } from './schema/resolve';
import type { EntityKind, FieldDef, FieldType, RelationTypeDef, SchemaSnapshot } from './schema/types';
import { parseMoneyRange, validateFieldValue } from './schema/values';

type Snap = Pick<SchemaSnapshot, 'types' | 'groups' | 'fields' | 'templates' | 'relationTypes' | 'lists'>;

/**
 * A claim as it will be written: which field or relation it is (`predicate`), read in which direction, and
 * everything the reviewer can adjust. Built from the extraction (`draftFromExtracted`), edited in review.
 */
export interface ClaimDraft {
  /** Field key of the subject's type, or relation type key. Null = nothing fits yet: the reviewer picks one. */
  predicate: string | null;
  /** Relations: the extracted object is the subject ("B is funded by A" read from "A funds B"). */
  reversed: boolean;
  /** Field value as text; relations: the role or a note ("Editorial director"). */
  value: string | null;
  /** Dates as written: a day, a range, "since 2021"… */
  period: string | null;
  /** Amount as written: "€50,000", "€2–10M", "undisclosed". */
  amount: string | null;
  claimKind: ClaimKind;
  attributionLevel: AttributionLevel;
  verificationFlag: VerificationFlag | null;
  highImportance: boolean;
  /** Author of an assessment. */
  assessedBy: string | null;
  /** Reliability grade: source reliability A–F … */
  sourceReliability: SourceReliability | null;
  /** … × information credibility 1–6. */
  credibility: number | null;
  /** Statements: the words as spoken or written, and a translation. */
  originalText: string | null;
  translation: string | null;
}

/** Step 2 decision for one claim: accept (as drafted, possibly edited) or reject. */
export type ClaimResolution = { action: 'accept'; claim: ClaimDraft } | { action: 'reject' };

/** Field types a claim from a document can fill with one value (tables and ratings are filled by their importers). */
export const CLAIM_FIELD_TYPES: readonly FieldType[] = [
  'text',
  'longtext',
  'number',
  'fuzzy_date',
  'enum',
  'multi_enum',
  'url',
  'email',
  'phone',
  'money_range',
  'social_handles',
  'entity_ref',
  'place_ref',
];

/** The field that holds statements (seeded on actors). */
export const STATEMENTS_FIELD = 'statements';

const MAX_TEXT = 4000;
const cleanText = (v: unknown, max = MAX_TEXT): string | null => {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return s ? s.slice(0, max) : null;
};

/** Validates the shape of a decision sent by the client (the API then checks it against the data). */
export function parseClaimResolution(input: unknown): { ok: true; value: ClaimResolution } | { ok: false; error: string } {
  if (!input || typeof input !== 'object') return { ok: false, error: 'Send a decision.' };
  const b = input as Record<string, unknown>;
  if (b.action === 'reject') return { ok: true, value: { action: 'reject' } };
  if (b.action !== 'accept') return { ok: false, error: 'Unknown decision.' };
  const c = (b.claim ?? {}) as Record<string, unknown>;
  if (!c || typeof c !== 'object') return { ok: false, error: 'Send the claim to accept.' };
  const claimKind = CLAIM_KINDS.includes(c.claimKind as ClaimKind) ? (c.claimKind as ClaimKind) : null;
  if (!claimKind) return { ok: false, error: 'Kind must be fact, statement, assessment or allegation.' };
  const attributionLevel = ATTRIBUTION_LEVELS.includes(c.attributionLevel as AttributionLevel)
    ? (c.attributionLevel as AttributionLevel)
    : 'on_record';
  const flag = c.verificationFlag ?? null;
  if (flag !== null && !VERIFICATION_FLAGS.includes(flag as VerificationFlag))
    return { ok: false, error: 'Verification must be documented, semi-documented or unverified.' };
  const letter = typeof c.sourceReliability === 'string' && c.sourceReliability ? c.sourceReliability.toUpperCase() : null;
  if (letter !== null && !isSourceReliability(letter)) return { ok: false, error: 'Source reliability must be A–F.' };
  const cred = c.credibility === null || c.credibility === undefined || c.credibility === '' ? null : Number(c.credibility);
  if (cred !== null && !isCredibility(cred)) return { ok: false, error: 'Credibility must be 1–6.' };
  return {
    ok: true,
    value: {
      action: 'accept',
      claim: {
        predicate: cleanText(c.predicate, 120),
        reversed: c.reversed === true,
        value: cleanText(c.value),
        period: cleanText(c.period, 200),
        amount: cleanText(c.amount, 200),
        claimKind,
        attributionLevel,
        verificationFlag: flag as VerificationFlag | null,
        highImportance: c.highImportance === true,
        assessedBy: cleanText(c.assessedBy, 300),
        sourceReliability: letter as SourceReliability | null,
        credibility: cred,
        originalText: cleanText(c.originalText),
        translation: cleanText(c.translation),
      },
    },
  };
}

/** The `candidates.decision` a step 2 decision is stored as. */
export const claimDecisionOf = (r: ClaimResolution, edited: boolean): 'accepted' | 'edited' | 'rejected' =>
  r.action === 'reject' ? 'rejected' : edited ? 'edited' : 'accepted';

/** Whether an accepted draft differs from the suggested one (→ `edited`). */
export function draftEdited(suggested: ClaimDraft, chosen: ClaimDraft): boolean {
  const keys = Object.keys(suggested) as (keyof ClaimDraft)[];
  return keys.some(k => (suggested[k] ?? null) !== (chosen[k] ?? null));
}

// ── Which field or relation a claim is ─────────────────────────────────────────────────────────────────────

export interface ClaimEnd {
  kind: EntityKind;
  /** Entity type id (null when unknown). */
  typeId: string | null;
}

/** Fields of the subject's type a claim can fill (inherited groups, not archived, single-value types). */
export function claimFields(schema: Snap, typeId: string | null): FieldDef[] {
  if (!typeId) return [];
  return groupsForType(schema, typeId)
    .flatMap(g => g.fields)
    .filter(f => !f.archived && CLAIM_FIELD_TYPES.includes(f.dataType));
}

const fitsKinds = (r: RelationTypeDef, s: EntityKind, o: EntityKind) =>
  (!r.sourceKinds.length || r.sourceKinds.includes(s)) && (!r.targetKinds.length || r.targetKinds.includes(o));

/** Relation types that can link these two ends, in either direction (`reversed` = read object → subject). */
export function claimRelations(schema: Snap, subject: EntityKind, object: EntityKind): { rel: RelationTypeDef; reversed: boolean }[] {
  const out: { rel: RelationTypeDef; reversed: boolean }[] = [];
  for (const rel of schema.relationTypes) {
    if (rel.archived) continue;
    if (fitsKinds(rel, subject, object)) out.push({ rel, reversed: false });
    else if (fitsKinds(rel, object, subject)) out.push({ rel, reversed: true });
  }
  return out;
}

const PREPOSITIONS = /\b(of|to|for|by|in|at|on|with|from|the|a|an|is|was|has|had|as)\b/g;
const words = (s: string) => foldText(s).replace(PREPOSITIONS, ' ').replace(/\s+/g, ' ').trim();
/** Like `words`, keeping "by": for links it carries the direction ("funded by" vs "funded"). */
const LINK_STOP = /\b(of|to|for|in|at|on|with|from|the|a|an|is|was|has|had|as)\b/g;
const relWords = (s: string) => foldText(s).replace(LINK_STOP, ' ').replace(/\s+/g, ' ').trim();

/** Common ways documents word an attribute → the seeded field key. */
const FIELD_SYNONYMS: Record<string, string> = {
  born: 'date_of_birth',
  birth: 'date_of_birth',
  'birth date': 'date_of_birth',
  birthday: 'date_of_birth',
  dob: 'date_of_birth',
  citizenship: 'nationality',
  role: 'current_position',
  position: 'current_position',
  job: 'current_position',
  title: 'current_position',
  occupation: 'current_position',
  party: 'political_group',
  'political party': 'political_group',
  'parliamentary group': 'political_group',
  'founding date': 'founded',
  foundation: 'founded',
  established: 'founded',
  creation: 'founded',
  created: 'founded',
  'founded in': 'founded',
  hq: 'headquarters',
  seat: 'headquarters',
  'based in': 'headquarters',
  country: 'main_country',
  'legal status': 'legal_form',
  budget: 'total_budget',
  revenue: 'total_budget',
  site: 'website',
  'web site': 'website',
  url: 'website',
  objective: 'stated_objectives',
  objectives: 'stated_objectives',
  goals: 'stated_objectives',
  mission: 'stated_objectives',
  interests: 'strategic_interests',
  themes: 'priority_themes',
  ideology: 'ideological_framework',
  'strategic role': 'strategic_analysis',
  analysis: 'strategic_analysis',
  assessment: 'strategic_analysis',
  vulnerability: 'vulnerabilities',
  weaknesses: 'vulnerabilities',
  'pressure points': 'vulnerabilities',
  'next steps': 'next_actions',
  priority: 'monitoring_priority',
  outcome: 'outcome',
  result: 'outcome',
};

/** Common ways documents word a link → relation type key (and whether the words read it backwards). */
const RELATION_SYNONYMS: Record<string, [key: string, reversed: boolean]> = {
  ceo: ['role_at', false],
  director: ['role_at', false],
  president: ['role_at', false],
  chair: ['role_at', false],
  chairman: ['role_at', false],
  chairwoman: ['role_at', false],
  founder: ['role_at', false],
  'co founder': ['role_at', false],
  head: ['role_at', false],
  editor: ['role_at', false],
  'editor chief': ['role_at', false],
  spokesperson: ['role_at', false],
  secretary: ['role_at', false],
  treasurer: ['role_at', false],
  'board member': ['role_at', false],
  trustee: ['role_at', false],
  manager: ['role_at', false],
  works: ['employer_of', true],
  employee: ['employer_of', true],
  employed: ['employer_of', false],
  staff: ['employer_of', true],
  employs: ['employer_of', false],
  owns: ['owner_of', false],
  owner: ['owner_of', false],
  owned: ['owner_of', false],
  backer: ['owner_of', false],
  funds: ['funded_by', true],
  finances: ['funded_by', true],
  financed: ['funded_by', true],
  funded: ['funded_by', true],
  funder: ['funded_by', true],
  donor: ['funded_by', true],
  sponsored: ['funded_by', true],
  'receives funding': ['funded_by', false],
  'received funding': ['funded_by', false],
  donated: ['donated_to', false],
  donation: ['donated_to', false],
  gave: ['donated_to', false],
  paid: ['paid', false],
  payment: ['paid', false],
  invested: ['invested_in', false],
  grant: ['granted', false],
  member: ['member_of', false],
  joined: ['member_of', false],
  affiliated: ['member_of', false],
  partner: ['event_partner_of', false],
  'media partner': ['media_platform_for', false],
  platform: ['media_platform_for', false],
  advises: ['adviser_to', false],
  advisor: ['adviser_to', false],
  assistant: ['assistant_to', false],
  colleague: ['colleague_of', false],
  coalition: ['coalition_with', false],
  ally: ['ideologically_aligned_with', false],
  aligned: ['ideologically_aligned_with', false],
  formerly: ['former_role_at', false],
  former: ['former_role_at', false],
  attended: ['attendee', false],
  attends: ['attendee', false],
  attending: ['attendee', false],
  participated: ['participant', false],
  participant: ['participant', false],
  'took part': ['participant', false],
  spoke: ['speaker', false],
  speaks: ['speaker', false],
  keynote: ['speaker', false],
  panelist: ['speaker', false],
  organised: ['organizer', false],
  organized: ['organizer', false],
  organiser: ['organizer', false],
  hosted: ['host', false],
  moderated: ['moderator', false],
  sponsor: ['sponsor', false],
  wrote: ['author', false],
  authored: ['author', false],
  published: ['author', false],
  voted: ['vote_for', false],
  // Harm events (D398): the perpetrator carried it out, the person or organization it was aimed at is targeted.
  perpetrator: ['carried_out', false],
  'carried out': ['carried_out', false],
  conducted: ['carried_out', false],
  orchestrated: ['carried_out', false],
  plaintiff: ['carried_out', false],
  claimant: ['carried_out', false],
  target: ['targeted_by', false],
  targeted: ['targeted_by', false],
  victim: ['targeted_by', false],
  defendant: ['targeted_by', false],
  held: ['held_at', false],
  venue: ['held_at', false],
  located: ['located_in', false],
  based: ['based_in', false],
  headquartered: ['based_in', false],
};

/** Relation types a word is most likely about, for claims of this shape. */
const SHAPE_CATEGORIES: Record<string, string[]> = {
  relation: ['relation', 'location', 'flow'],
  participation: ['participation'],
  flow: ['flow', 'relation'],
};

/** Default relation type when the wording names none (participation: "participant in"). */
const SHAPE_FALLBACK: Record<string, string> = { participation: 'participant' };

export interface PredicateGuess {
  predicate: string | null;
  reversed: boolean;
  /** How it was found (shown on the card): the words matched a label, a synonym, or it is the shape's default. */
  how: 'label' | 'synonym' | 'default' | null;
}

/** Relation types that are a Money flows edge (packages/db/src/claims/flows.ts reads them). */
const MONEY_RELATIONS = new Set(['funded_by', 'donated_to', 'paid', 'invested_in', 'granted']);

/**
 * True when review would read this raw relation wording as a money relation: its label or key, or one of the
 * synonyms below ("financed by", "sponsored", "donor"). Used by the extraction evals' no-flow check.
 */
export function isMoneyWording(predicate: string): boolean {
  const bare = relWords(predicate)
    .replace(/(^| )by$/, '')
    .trim();
  if (MONEY_RELATIONS.has(bare.replace(/ /g, '_'))) return true;
  return [bare, ...bare.split(' ')].some(w => MONEY_RELATIONS.has(RELATION_SYNONYMS[w]?.[0] ?? ''));
}

/**
 * The field or relation type an extracted claim most likely is, given its shape and the wording of its predicate:
 * exact label / key first, then a label contained in the wording (longest wins), then common synonyms, then the
 * shape's default (assessments → Strategic analysis; participation → participant in). Relations read backwards
 * when the wording matches the inverse label ("funds" = inverse of "funded by") or the kinds only fit that way.
 */
export function suggestPredicate(
  schema: Snap,
  claim: { shape: string; predicate: string; claimKind: ClaimKind },
  subject: ClaimEnd,
  object: ClaimEnd | null,
): PredicateGuess {
  const none: PredicateGuess = { predicate: null, reversed: false, how: null };
  const text = words(claim.predicate ?? '');
  if (claim.shape === 'statement' || claim.claimKind === 'statement') {
    const f = claimFields(schema, subject.typeId).find(x => x.key === STATEMENTS_FIELD);
    return f ? { predicate: f.key, reversed: false, how: 'default' } : none;
  }
  if (!object || claim.shape === 'attribute') {
    const fields = claimFields(schema, subject.typeId);
    const byKey = (key: string | undefined) => (key ? fields.find(f => f.key === key) : undefined);
    const exact = fields.find(f => words(f.label) === text || words(f.key.replace(/_/g, ' ')) === text);
    if (exact) return { predicate: exact.key, reversed: false, how: 'label' };
    const syn = byKey(FIELD_SYNONYMS[text]) ?? byKey(FIELD_SYNONYMS[foldText(claim.predicate ?? '')]);
    if (syn) return { predicate: syn.key, reversed: false, how: 'synonym' };
    const inside = fields
      .map(f => ({ f, n: words(f.label) }))
      .filter(x => x.n.length > 3 && text.length > 3 && (` ${text} `.includes(` ${x.n} `) || ` ${x.n} `.includes(` ${text} `)))
      .sort((a, b) => b.n.length - a.n.length)[0];
    if (inside) return { predicate: inside.f.key, reversed: false, how: 'label' };
    for (const w of text.split(' ')) {
      const hit = byKey(FIELD_SYNONYMS[w]);
      if (hit) return { predicate: hit.key, reversed: false, how: 'synonym' };
    }
    if (claim.claimKind === 'assessment') {
      const a = byKey('strategic_analysis');
      if (a) return { predicate: a.key, reversed: false, how: 'default' };
    }
    return none;
  }
  const options = claimRelations(schema, subject.kind, object.kind);
  if (!options.length) return none;
  const categories = SHAPE_CATEGORIES[claim.shape] ?? ['relation', 'location', 'flow', 'participation'];
  const ranked = [...options].sort(
    (a, b) =>
      (categories.includes(a.rel.category) ? categories.indexOf(a.rel.category) : 9) -
      (categories.includes(b.rel.category) ? categories.indexOf(b.rel.category) : 9),
  );
  const pick = (key: string, wordsBackwards: boolean): PredicateGuess | null => {
    const o = ranked.find(x => x.rel.key === key);
    if (!o) return null;
    // Symmetric types read the same both ways; otherwise the direction of the words and of the kinds combine.
    const reversed = o.rel.symmetric ? false : o.reversed !== wordsBackwards;
    if (!o.rel.symmetric && !fitsKinds(o.rel, reversed ? object.kind : subject.kind, reversed ? subject.kind : object.kind)) return null;
    return { predicate: key, reversed, how: null };
  };
  // Links keep "by": "funded by" is the label, "funded" (A funded B) reads it backwards; a passive "… by" flips a
  // synonym's direction ("financed by" = funded by).
  const link = relWords(claim.predicate ?? '');
  const passive = / by$/.test(` ${link}`);
  const bare = passive ? link.replace(/(^| )by$/, '').trim() : link;
  const synonym = (w: string) => {
    const s = RELATION_SYNONYMS[w];
    return s ? pick(s[0], s[1] !== passive) : null;
  };
  if (link) {
    for (const { rel } of ranked) {
      const forward = [relWords(rel.label), relWords(rel.key.replace(/_/g, ' '))];
      if (forward.includes(link)) {
        const g = pick(rel.key, false);
        if (g) return { ...g, how: 'label' };
      }
      if (rel.inverseLabel && !rel.symmetric && relWords(rel.inverseLabel) === link) {
        const g = pick(rel.key, true);
        if (g) return { ...g, how: 'label' };
      }
    }
    const syn = synonym(bare);
    if (syn) return { ...syn, how: 'synonym' };
    // A label inside the wording ("event partner (former)"), longest first; inverse labels read backwards.
    const labels = ranked
      .flatMap(({ rel }) => [
        { key: rel.key, n: relWords(rel.label), back: false },
        ...(rel.inverseLabel && !rel.symmetric ? [{ key: rel.key, n: relWords(rel.inverseLabel), back: true }] : []),
      ])
      .filter(x => x.n.length > 3 && ` ${link} `.includes(` ${x.n} `))
      .sort((a, b) => b.n.length - a.n.length);
    for (const l of labels) {
      const g = pick(l.key, l.back);
      if (g) return { ...g, how: 'label' };
    }
    for (const w of bare.split(' ')) {
      const g = synonym(w);
      if (g) return { ...g, how: 'synonym' };
    }
  }
  const fallback = SHAPE_FALLBACK[claim.shape];
  const g = fallback ? pick(fallback, false) : null;
  return g ? { ...g, how: 'default' } : none;
}

// ── From extraction to draft ───────────────────────────────────────────────────────────────────────────────

/** An extracted claim as stored on the candidate (only what step 2 reads). */
export interface ExtractedClaimLike {
  shape: string;
  kind?: string | null;
  predicate?: string | null;
  value?: string | null;
  text?: string | null;
  date?: string | null;
  endDate?: string | null;
  amount?: string | null;
  author?: string | null;
  attribution?: string | null;
  verification?: string | null;
  highImportance?: boolean | null;
  originalText?: string | null;
  translation?: string | null;
}

/** The dates of an extracted claim as one period ("2019" + "2022" → "2019–2022"). */
export function periodOf(c: Pick<ExtractedClaimLike, 'date' | 'endDate'>): string | null {
  const from = cleanText(c.date, 200);
  const to = cleanText(c.endDate, 200);
  if (from && to && from !== to) return `${from}–${to}`;
  return from ?? to;
}

/** The grade a claim starts with (from its cited publisher, its source, or the collection's default). */
export interface GradeDefault {
  sourceReliability: SourceReliability | null;
  credibility: number | null;
  /** Where the letter comes from, in plain words ("Le Monde, the cited publisher"). */
  from: string | null;
}

/** The suggested draft for an extracted claim: predicate guessed, dates and amounts as written, grade defaulted. */
export function draftFromExtracted(
  schema: Snap,
  c: ExtractedClaimLike,
  subject: ClaimEnd,
  object: ClaimEnd | null,
  grade: GradeDefault = { sourceReliability: null, credibility: null, from: null },
): { draft: ClaimDraft; guess: PredicateGuess } {
  const claimKind: ClaimKind = CLAIM_KINDS.includes(c.kind as ClaimKind) ? (c.kind as ClaimKind) : 'fact';
  const guess = suggestPredicate(schema, { shape: c.shape, predicate: c.predicate ?? '', claimKind }, subject, object);
  const field = guess.predicate ? claimFields(schema, subject.typeId).find(f => f.key === guess.predicate) : undefined;
  const rel = guess.predicate && !field ? schema.relationTypes.find(r => r.key === guess.predicate) : undefined;
  const period = periodOf(c);
  const statement = claimKind === 'statement' || c.shape === 'statement';
  let value = cleanText(c.value);
  let draftPeriod = period;
  let amount = cleanText(c.amount, 200);
  if (field?.dataType === 'fuzzy_date') {
    // "Date of birth: 2 March 1971": the date is the value, not a validity period.
    value = value ?? period;
    draftPeriod = null;
  }
  if (field?.dataType === 'money_range') {
    value = value ?? amount;
    amount = null;
  }
  if (field && statement) value = null;
  if (field && amount) amount = null;
  if (rel) {
    // A role written as the predicate ("CEO of") is kept on the relation.
    if (!value && rel.key === 'role_at' && guess.how !== 'label')
      value = cleanText(c.predicate, 300)?.replace(/\s+(of|at|for|in|to|with)$/i, '') ?? null;
    if (!rel.hasAmount) amount = null;
  }
  const originalText = statement ? (cleanText(c.originalText) ?? cleanText(c.value) ?? cleanText(c.text)) : null;
  return {
    guess,
    draft: {
      predicate: guess.predicate,
      reversed: guess.reversed,
      value: statement ? null : value,
      period: draftPeriod,
      amount,
      claimKind: statement ? 'statement' : claimKind,
      attributionLevel: ATTRIBUTION_LEVELS.includes(c.attribution as AttributionLevel) ? (c.attribution as AttributionLevel) : 'on_record',
      verificationFlag: VERIFICATION_FLAGS.includes(c.verification as VerificationFlag)
        ? (c.verification as VerificationFlag)
        : claimKind === 'allegation'
          ? 'unverified'
          : null,
      highImportance: c.highImportance === true,
      assessedBy: claimKind === 'assessment' ? cleanText(c.author, 300) : null,
      sourceReliability: grade.sourceReliability,
      credibility: grade.credibility,
      originalText,
      translation: statement ? cleanText(c.translation) : null,
    },
  };
}

/**
 * What stops a draft from being written (plain language, empty = fine). Mirrors the claims service: the field
 * belongs to the subject's type and the value fits it, the relation links these kinds, the period and the amount
 * are understood, a statement keeps its words.
 */
export function checkClaimDraft(schema: Snap, d: ClaimDraft, subject: ClaimEnd, object: ClaimEnd | null): string[] {
  const errors: string[] = [];
  if (!d.predicate) return ['Pick the field or relation this claim fills.'];
  const field = claimFields(schema, subject.typeId).find(f => f.key === d.predicate);
  const rel = field ? undefined : schema.relationTypes.find(r => r.key === d.predicate && !r.archived);
  if (!field && !rel) return ['That field or relation does not fit this entity’s type.'];
  if (d.period && !parseFuzzyDate(d.period)) errors.push('Dates not recognised. Try 2019–2022, "since 2021" or "c. 2021".');
  if (d.amount && !parseMoneyRange(d.amount)) errors.push('Amount not recognised. Try "€50,000", "€2–10M", "est. $1M" or "undisclosed".');
  if (d.claimKind === 'statement' && !d.originalText) errors.push('A statement keeps the words as said or written (original text).');
  if (field) {
    const raw = d.claimKind === 'statement' ? (d.value ?? d.originalText) : d.value;
    if (!raw) errors.push(`Give a value for ${field.label}.`);
    else {
      const r = validateFieldValue({ ...field, required: true, multiValued: false }, raw, schema);
      if (!r.ok) errors.push(...r.errors);
    }
  }
  if (rel) {
    if (!object) errors.push(`“${rel.label}” links two entities, but this claim names only one.`);
    else {
      const [s, o] = d.reversed ? [object.kind, subject.kind] : [subject.kind, object.kind];
      if (!fitsKinds(rel, s, o) && !(rel.symmetric && fitsKinds(rel, o, s)))
        errors.push(`“${rel.label}” links ${rel.sourceKinds.join('/') || 'any'} → ${rel.targetKinds.join('/') || 'any'}, not ${s} → ${o}.`);
    }
    if (d.amount && !rel.hasAmount) errors.push(`“${rel.label}” has no amount.`);
  }
  return errors;
}

/** Label of a predicate for cards and filters: the relation type's label, or the field's. */
export function predicateLabel(schema: Pick<SchemaSnapshot, 'fields' | 'relationTypes'>, predicate: string | null) {
  if (!predicate) return 'Not mapped yet';
  return schema.relationTypes.find(r => r.key === predicate)?.label ?? schema.fields.find(f => f.key === predicate)?.label ?? predicate;
}

/** Confidence threshold of the "accept all high-confidence claims" bulk action (PLAN §6: "accept all ≥ 0.9"). */
export const HIGH_CONFIDENCE = 0.9;

/**
 * A reviewed draft as a claims-service input (no evidence, confidence or status): a field of the subject's type
 * first, else a relation type (same precedence as `checkClaimDraft`); a reversed relation swaps its ends.
 */
export function inputOfDraft(
  schema: Snap,
  d: ClaimDraft,
  subjectTypeId: string | null,
  subjectId: string,
  objectId: string | null,
): { subjectId: string; input: ClaimInput } {
  const isField = claimFields(schema, subjectTypeId).some(f => f.key === d.predicate);
  let [s, o] = [subjectId, objectId];
  if (!isField && d.reversed && o) [s, o] = [o, s];
  const relationValue = d.value ? (d.predicate === 'role_at' ? { role: d.value } : { note: d.value }) : null;
  return {
    subjectId: s,
    input: {
      predicate: d.predicate ?? '',
      value: isField ? (d.claimKind === 'statement' ? (d.value ?? d.originalText) : d.value) : relationValue,
      objectEntityId: isField ? null : o,
      claimKind: d.claimKind,
      period: d.period,
      amount: d.amount,
      attributionLevel: d.attributionLevel,
      verificationFlag: d.verificationFlag,
      highImportance: d.highImportance,
      assessedBy: d.assessedBy,
      sourceReliability: d.sourceReliability,
      credibility: d.credibility,
      originalText: d.originalText,
      translation: d.translation,
    },
  };
}
